import React, { ReactElement, useEffect, useRef } from "react";
import style from "./style.module.scss";
import { Color } from "@/src/views/styles/typescript/types/color";
import { WordStyling } from "@/src/types/landingPage";
import purify from 'dompurify';

type Variant =
  | "heading-1-medium"
  | "heading-1-demibold"
  | "heading-1-bold"
  | "heading-2-medium"
  | "heading-2-demibold"
  | "heading-2-bold"
  | "heading-3-medium"
  | "heading-3-demibold"
  | "heading-3-bold"
  | "title-4-bold"
  | "title-4-semibold"
  | "title-4-medium"
  | "title-4-regular"
  | "title-2-bold"
  | "title-2-semibold"
  | "title-2-medium"
  | "title-2-regular"
  | "subtitle-1-medium"
  | "subtitle-1-demibold-1"
  | "subtitle-1-demibold-2"
  | "subtitle-1-bold"
  | "subtitle-2-medium"
  | "subtitle-2-demibold"
  | "subtitle-2-bold"
  | "subtitle-3-medium-1"
  | "subtitle-3-medium-2"
  | "subtitle-3-demibold"
  | "subtitle-3-bold-1"
  | "subtitle-3-bold-2"
  | "subtitle-3-regular"
  | "subtitle-4-medium"
  | "subtitle-4-demibold"
  | "subtitle-4-bold"
  | "body-3-medium"
  | "body-3-semibold"
  | "body-3-bold"
  | "body-regular"
  | "body-medium-1"
  | "body-medium-2"
  | "body-medium-3"
  | "body-demibold-1"
  | "body-demibold-2"
  | "body-bold"
  | "caption-1-medium"
  | "caption-regular"
  | "caption-medium"
  | "caption-demibold"
  | "caption-bold"
  | "heading-4-regular"
  | "heading-4-medium"
  | "heading-4-semibold"
  | "heading-4-bold"
  | "heading-5-regular"
  | "heading-5-medium"
  | "heading-5-semibold"
  | "heading-5-bold"
  | "heading-6-regular"
  | "heading-6-medium"
  | "heading-6-semibold"
  | "heading-6-bold"
  | "subtitle-5-regular"
  | "subtitle-5-medium"
  | "subtitle-5-semibold"
  | "subtitle-5-bold"
  | "subtitle-6-regular"
  | "subtitle-6-medium"
  | "subtitle-6-semibold"
  | "subtitle-6-bold";

type Align = "left" | "right" | "center";
const Typography = ({
  children,
  ...props
}: {
  children?: JSX.Element | string;
  variant?: Variant;
  id?: string;
  align?: Align;
  color?: Color;
  device?: string;
  paragraph?: boolean;
  fontStyle?: "italic" | "default";
  listWordStyling?: WordStyling[];
}): ReactElement => {
  const typographyDivRef = useRef<HTMLDivElement>()
  const typographySpanRef = useRef<HTMLSpanElement>()
  const deviceFontVariant: string = `typography-web--${props.variant}`;

  const fontStyles: string = `${style.typography} ${style["typography-color--" + props.color]} ${style[deviceFontVariant]} ${style["typography-align--" + props.align]}`;

  useEffect(() => {
    if (typeof children !== 'string' ||
      !props.listWordStyling) {
      return
    }

    const htmlString = props.listWordStyling?.reduce((html, item) => {
      const regex = new RegExp(`\\b${item.word}\\b`, 'gi');
      return html.replace(regex, `<span style="font-style: ${item.style};">${item.word}</span>`);
    }, children);
    const sanitizedHtml = purify.sanitize(htmlString)
    if (typographyDivRef.current) {
      typographyDivRef.current.innerHTML = sanitizedHtml
    }
    if (typographySpanRef.current) {
      typographySpanRef.current.innerHTML = sanitizedHtml
    }
  }, [children, props.listWordStyling])

  return (
    <>
      {props.paragraph ? (
        <div
          id={props.id}
          ref={typographyDivRef}
          className={fontStyles}
          style={{ fontStyle: props.fontStyle }}
        >{children}</div>
      ) : (
        <span
          id={props.id}
          ref={typographySpanRef}
          className={fontStyles}
          style={{ fontStyle: props.fontStyle }}
        >{children}</span>
      )}
    </>
  );
};

Typography.defaultProps = {
  device: "mobile",
  color: "primary-00",
  align: "left",
  variant: "body-1-bold",
  paragraph: false,
  id: "typography",
};
export default Typography;
